import { useState, useRef } from 'react';
import { Plus, X, Image as ImageIcon, Loader2, Pencil, ZoomIn, Camera } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from '@/lib/toast';
import { LIMITS, ERROR_MESSAGES } from '@/lib/constants';
import {
  type AtaImagem,
  uploadAtaImagem,
  deleteAtaImagem,
  updateAtaImagem,
} from '@/services/ataImagem.service';

const MAX_IMAGENS = LIMITS.ATA_IMAGES_MAX;

interface AtaGaleriaEditorProps {
  ataId: string;
  imagens: AtaImagem[];
  onImagensChange: (imagens: AtaImagem[]) => void;
  disabled?: boolean;
}

export function AtaGaleriaEditor({
  ataId,
  imagens,
  onImagensChange,
  disabled = false,
}: AtaGaleriaEditorProps) {
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editDescricao, setEditDescricao] = useState('');
  const [lightboxImage, setLightboxImage] = useState<AtaImagem | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error(ERROR_MESSAGES.UPLOAD_INVALID_TYPE);
      return;
    }

    if (file.size > LIMITS.IMAGE_MAX_SIZE_BYTES) {
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
      toast.error(`Imagem muito grande (${fileSizeMB}MB). O tamanho máximo permitido é ${LIMITS.IMAGE_MAX_SIZE_MB}MB. Reduza o tamanho da imagem antes de enviar.`);
      return;
    }

    if (imagens.length >= MAX_IMAGENS) {
      toast.error(ERROR_MESSAGES.MAX_IMAGES_REACHED);
      return;
    }

    setUploading(true);
    try {
      const novaImagem = await uploadAtaImagem(ataId, file);
      onImagensChange([...imagens, novaImagem]);
      setEditingId(novaImagem.id);
      setEditDescricao('');
    } catch (error: any) {
      const message = error.response?.data?.message || 'Não foi possível enviar a imagem. Verifique sua conexão e tente novamente.';
      toast.error(message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDelete = async (imagemId: number) => {
    setDeletingId(imagemId);
    try {
      await deleteAtaImagem(ataId, imagemId);
      onImagensChange(imagens.filter((img) => img.id !== imagemId));
      toast.success('Imagem removida com sucesso.');
    } catch (error) {
      toast.error('Não foi possível remover a imagem. Tente novamente.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleSaveDescricao = async (imagemId: number) => {
    try {
      const updated = await updateAtaImagem(ataId, imagemId, { descricao: editDescricao });
      onImagensChange(imagens.map((img) => (img.id === imagemId ? updated : img)));
      setEditingId(null);
      setEditDescricao('');
    } catch (error) {
      toast.error('Não foi possível salvar a descrição da imagem. Tente novamente.');
    }
  };

  const startEditing = (imagem: AtaImagem) => {
    setEditingId(imagem.id);
    setEditDescricao(imagem.descricao || '');
  };

  const canAddMore = imagens.length < MAX_IMAGENS;

  return (
    <>
      {/* Lightbox Modal */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 z-[9999] bg-black/85 flex items-center justify-center p-4 animate-in fade-in duration-300"
          onClick={() => setLightboxImage(null)}
        >
          <button
            onClick={() => setLightboxImage(null)}
            className="absolute top-6 right-6 p-3 rounded-full bg-white/5 hover:bg-white/10 text-white/70 hover:text-white transition-all duration-200"
          >
            <X className="h-5 w-5" />
          </button>
          <div className="max-w-5xl max-h-[90vh] animate-in zoom-in-95 duration-300">
            <img
              src={lightboxImage.url}
              alt={lightboxImage.descricao || 'Imagem ampliada'}
              className="max-w-full max-h-[85vh] object-contain rounded-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            {lightboxImage.descricao && (
              <p className="text-white/80 text-center mt-6 text-sm font-light tracking-wide">
                {lightboxImage.descricao}
              </p>
            )}
          </div>
        </div>
      )}

      <div className="mt-8">
        {/* Header minimalista */}
        <div className="flex items-center justify-between mb-6 gap-4">
          <div className="flex items-center gap-3 shrink-0">
            <h3 className="text-sm font-medium text-foreground tracking-tight">
              Registro Fotográfico
            </h3>
            <span className={`text-xs font-medium px-1.5 py-0.5 rounded-md ${
              imagens.length >= MAX_IMAGENS
                ? 'bg-red-100 text-red-600'
                : imagens.length >= MAX_IMAGENS * 0.8
                ? 'bg-amber-100 text-amber-600'
                : 'bg-muted text-muted-foreground'
            }`}>
              {imagens.length}/{MAX_IMAGENS}
            </span>
          </div>

          {/* Barra de progresso */}
          <div className="flex-1 flex items-center gap-2 min-w-0">
            <div className="relative flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 rounded-full transition-all duration-500"
                style={{
                  width: `${(imagens.length / MAX_IMAGENS) * 100}%`,
                  background: imagens.length >= MAX_IMAGENS
                    ? '#ef4444'
                    : imagens.length >= MAX_IMAGENS * 0.75
                    ? `linear-gradient(to right, #22c55e, #eab308, #f97316, #ef4444)`
                    : imagens.length >= MAX_IMAGENS * 0.5
                    ? `linear-gradient(to right, #22c55e, #eab308, #f97316)`
                    : imagens.length >= MAX_IMAGENS * 0.25
                    ? `linear-gradient(to right, #22c55e, #eab308)`
                    : '#22c55e',
                }}
              />
            </div>
            <span className="text-[10px] text-muted-foreground shrink-0">
              {Math.round((imagens.length / MAX_IMAGENS) * 100)}%
            </span>
          </div>
        </div>

        {/* Banner de limite atingido */}
        {imagens.length >= MAX_IMAGENS && !disabled && (
          <div className="flex items-center gap-2.5 mb-4 px-3 py-2.5 rounded-xl bg-red-50 border border-red-100">
            <ImageIcon className="h-4 w-4 text-red-500 shrink-0" />
            <p className="text-xs text-red-600">
              Limite de {MAX_IMAGENS} imagens atingido. Remova uma imagem para adicionar novas.
            </p>
          </div>
        )}

        {imagens.length === 0 && !disabled ? (
          /* Empty state minimalista */
          <div 
            onClick={() => fileInputRef.current?.click()}
            className="relative border border-dashed border-border rounded-2xl p-12 text-center cursor-pointer hover:border-primary/40 hover:bg-primary/5 transition-all duration-300 group"
          >
            <div className="w-14 h-14 mx-auto mb-5 rounded-2xl bg-muted group-hover:bg-muted/80 flex items-center justify-center transition-all duration-300">
              <Plus className="h-6 w-6 text-muted-foreground group-hover:text-foreground transition-colors" />
            </div>
            <p className="text-sm font-medium text-foreground mb-1">Adicionar fotos</p>
            <p className="text-xs text-muted-foreground font-light">
              Clique ou arraste imagens aqui
            </p>
          </div>
        ) : (
          /* Masonry-like grid */
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-3">
            {imagens.map((imagem, index) => (
              <div
                key={imagem.id}
                className="group relative"
              >
                {/* Container da imagem */}
                <div 
                  className="relative aspect-square rounded-xl overflow-hidden bg-muted cursor-pointer"
                  onClick={() => setLightboxImage(imagem)}
                >
                  <img
                    src={imagem.url}
                    alt={imagem.descricao || `Imagem ${index + 1}`}
                    className="w-full h-full object-cover transition-all duration-500 group-hover:scale-105"
                  />
                  
                  {/* Overlay sutil */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300" />
                  
                  {/* Número - sempre visível, discreto */}
                  <div className="absolute bottom-2 left-2 px-2 py-0.5 rounded-md bg-background/90 backdrop-blur-sm">
                    <span className="text-[10px] font-semibold text-foreground">{index + 1}</span>
                  </div>
                  
                  {/* Ações no hover */}
                  <div className="absolute top-2 right-2 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-all duration-200 translate-y-1 group-hover:translate-y-0">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setLightboxImage(imagem);
                      }}
                      className="p-2 bg-background/95 backdrop-blur-sm rounded-lg text-foreground/70 hover:text-foreground transition-colors shadow-sm"
                      title="Ampliar"
                    >
                      <ZoomIn className="h-3.5 w-3.5" />
                    </button>
                    {!disabled && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          startEditing(imagem);
                        }}
                        className="p-2 bg-background/95 backdrop-blur-sm rounded-lg text-foreground/70 hover:text-primary transition-colors shadow-sm"
                        title="Editar legenda"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                    )}
                    {!disabled && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(imagem.id);
                        }}
                        disabled={deletingId === imagem.id}
                        className="p-2 bg-background/95 backdrop-blur-sm rounded-lg text-foreground/70 hover:text-destructive disabled:opacity-50 transition-colors shadow-sm"
                        title="Remover"
                      >
                        {deletingId === imagem.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <X className="h-3.5 w-3.5" />
                        )}
                      </button>
                    )}
                  </div>
                </div>
                
                {/* Legenda - clean design */}
                {editingId === imagem.id ? (
                  <div className="mt-2">
                    <Input
                      value={editDescricao}
                      onChange={(e) => setEditDescricao(e.target.value)}
                      maxLength={LIMITS.IMAGE_DESCRIPTION_MAX}
                      placeholder="Adicionar legenda..."
                      className="text-xs h-8 border-border focus:border-primary/50 focus:ring-0 rounded-lg"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSaveDescricao(imagem.id);
                        } else if (e.key === 'Escape') {
                          setEditingId(null);
                        }
                      }}
                      onBlur={() => {
                        if (editDescricao !== imagem.descricao) {
                          handleSaveDescricao(imagem.id);
                        } else {
                          setEditingId(null);
                        }
                      }}
                    />
                  </div>
                ) : (
                  <div 
                    className={`mt-2 group/caption flex items-start gap-1 ${!disabled ? 'cursor-text' : ''}`}
                    onClick={() => !disabled && startEditing(imagem)}
                  >
                    <p className={`flex-1 text-xs leading-relaxed break-all ${
                      imagem.descricao
                        ? 'text-muted-foreground'
                        : 'text-muted-foreground/40 italic'
                    }`}>
                      {imagem.descricao || (disabled ? '—' : 'Clique para nomear...')}
                    </p>
                    {!disabled && (
                      <Pencil className="h-3 w-3 text-muted-foreground/30 group-hover/caption:text-muted-foreground shrink-0 mt-0.5 transition-colors duration-150" />
                    )}
                  </div>
                )}
              </div>
            ))}

            {/* Botão de adicionar - oculto ao atingir limite */}
            {canAddMore && !disabled && (
              <div
                onClick={() => !uploading && fileInputRef.current?.click()}
                className={`aspect-square rounded-xl border border-dashed border-border flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-primary/40 hover:bg-primary/5 transition-all duration-300 group ${
                  uploading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {uploading ? (
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                ) : (
                  <>
                    <div className="w-10 h-10 rounded-xl bg-muted group-hover:bg-muted/80 flex items-center justify-center transition-all duration-300">
                      <Plus className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
        />

        {imagens.length === 0 && disabled && (
          <div className="text-center py-12">
            <p className="text-sm text-muted-foreground font-light">
              Nenhuma imagem
            </p>
          </div>
        )}
      </div>
    </>
  );
}
